import React from 'react';
import MetricService from 'services/data/MetricService';
import { normalMetricMessages, inverseMetricMessages } from './utils/messages.ts';
import { makeWaitForData } from 'components/shared/WaitForData';
import { orZero } from 'lib/utils/NumUtils';
import { today, yesterday } from 'lib/utils/DateUtils.js';
import './style.scss';

const STREAK_DEPS = () => ['user', 'goals', 'account-data-formatted-last-7'];
const STREAK_METRICS = [
    'dailyPurchasesGoal', 'dailyRevenueGoal', 'dailyAdspendGoal', 'dailyRoasGoal', 'dailyCppGoal', 'dailyAovGoal',
    'yesterdayOrders', 'yesterdayRevenue', 'yesterdayAdspend', 'yesterdayRoas', 'yesterdayCpp', 'yesterdayAov'
];

const tabs = ['purchases', 'revenue', 'adspend', 'roas', 'cpp', 'aov'];
const dropdownTabs = [
    { value: 'purchases', label: 'Orders' },
    { value: 'revenue', label: 'Revenue' },
    { value: 'adspend', label: 'AdSpend' },
    { value: 'roas', label: 'MER' },
    { value: 'cpp', label: 'Cost Per Order' },
    { value: 'aov', label: 'AOV' }
];

const StreakMessaging = (props) => {

    const {
        dailyPurchasesGoal, dailyRevenueGoal, dailyAdspendGoal, dailyRoasGoal, dailyCppGoal, dailyAovGoal,
        yesterdayOrders, yesterdayRevenue, yesterdayAdspend, yesterdayRoas, yesterdayCpp, yesterdayAov
    } = MetricService.getSet(STREAK_METRICS);

    const renderDynamicMessage = () => {
        const checkStreak = (value, arr, swapped) => {
            let i = 0;

            for (j = arr.length - 1; j >= 0; j--) {
                if (!swapped) {
                    if (arr[j] >= value) {
                        i++;
                    } else {
                        return i;
                    }
                } else {
                    if (arr[j] <= value) {
                        i++;
                    } else {
                        return i;
                    }
                }
            }

            return i;
        }

        let goal = 0;
        let previousValues = props.deps['account-data-formatted-last-7'].days;
        let reversed = false;
        let currentTab = props.tab;
        let valueFunction; // @NOTE: Some dupe code with these assigned elsewhere, but ideally we get all this from the service anyhow, so refactor.

        if (typeof previousValues === 'undefined' || !previousValues || previousValues.length <= 0) {
            return "Everything looks good!";
        }

        switch (currentTab) {
            case 'purchases':
                valueFunction = x => parseFloat(x.totals.orders);
                goal = dailyPurchasesGoal;
                previousValues = previousValues.map(valueFunction);
                break;

            case 'revenue':
                valueFunction = x => parseFloat(x.totals.revenue);
                goal = dailyRevenueGoal;
                previousValues = previousValues.map(valueFunction);
                break;

            case 'adspend':
                valueFunction = x => parseFloat(x.totals.spend);
                goal = dailyAdspendGoal;
                previousValues = previousValues.map(valueFunction);
                break;

            case 'roas':
                valueFunction = x => orZero(x.totals.roas);
                goal = dailyRoasGoal;
                previousValues = previousValues.map(valueFunction);
                break;

            case 'cpp':
                valueFunction = x => orZero(x.totals.cpp);
                goal = dailyCppGoal;
                previousValues = previousValues.map(valueFunction);
                reversed = true;
                break;

            case 'aov':
                valueFunction = x => orZero(x.totals.aov);
                goal = dailyAovGoal;
                previousValues = previousValues.map(valueFunction);
                break;
        }

        const streak = checkStreak(goal, previousValues, reversed);

        if (streak >= 3) {
            // TODO: Don't tie this lookup here or in this way
            const metricLabel = dropdownTabs[tabs.indexOf(currentTab)].label;

            return `${streak} days in a row, ${metricLabel} has been on track!`;
        }

        const renderMetricMessage = (percentageGoal, messages) => {
            const { wayDown, down, slightlyDown, slightlyUp, up, wayUp } = messages;
            if (percentageGoal < -33) {
                return wayDown[Math.floor(Math.random() * wayDown.length)]
            } else if (percentageGoal >= -33 && percentageGoal < -10) {
                return down[Math.floor(Math.random() * down.length)]
            } else if (percentageGoal >= -10 && percentageGoal < 0) {
                return slightlyDown[Math.floor(Math.random() * slightlyDown.length)]
            } else if (percentageGoal < 10 && percentageGoal >= 0) {
                return slightlyUp[Math.floor(Math.random() * slightlyUp.length)]
            } else if (percentageGoal >= 10 && percentageGoal < 33) {
                return up[Math.floor(Math.random() * up.length)]
            } else if (percentageGoal >= 33) {
                return wayUp[Math.floor(Math.random() * wayUp.length)]
            } else {
                return ''
            }
        };
        let percentageGoal;

        // @TODO: enum the possible tab values, which are: purchases, revenue, adspend, roas, cpp, aov
        switch (currentTab) {
            case 'purchases':
                percentageGoal = parseFloat(parseFloat((yesterdayOrders - dailyPurchasesGoal) / dailyPurchasesGoal) * 100).toFixed(2);
                return `Orders ${renderMetricMessage(percentageGoal, normalMetricMessages)}`;
            case 'revenue':
                percentageGoal = parseFloat(parseFloat((yesterdayRevenue - dailyRevenueGoal) / dailyRevenueGoal) * 100).toFixed(2);
                return ` Store Revenue ${renderMetricMessage(percentageGoal, normalMetricMessages)}`;
            case 'adspend':
                percentageGoal = parseFloat(parseFloat((yesterdayAdspend - dailyAdspendGoal) / dailyAdspendGoal) * 100).toFixed(2);
                return `Adspend ${renderMetricMessage(percentageGoal, normalMetricMessages)}`;
            case 'roas':
                percentageGoal = parseFloat(parseFloat((yesterdayRoas - dailyRoasGoal) / dailyRoasGoal) * 100).toFixed(2);
                return `MER ${renderMetricMessage(percentageGoal, normalMetricMessages)}`;
            case 'cpp':
                percentageGoal = parseFloat(parseFloat((yesterdayCpp - dailyCppGoal) / dailyCppGoal) * 100).toFixed(2);
                return `Cost Per Order ${renderMetricMessage(percentageGoal, inverseMetricMessages)}`;
            case 'aov':
                percentageGoal = parseFloat(parseFloat((yesterdayAov - dailyAovGoal) / dailyAovGoal) * 100).toFixed(2);
                return `AOV ${renderMetricMessage(percentageGoal, normalMetricMessages)}`;
            default:
                return '';
        }
    }

    return (
        <div className="streak-messaging">
            {renderDynamicMessage()}
        </div>
    );
}

const StreakSkeleton = (props) => {
    return (
        <div className="streak-messaging skeleton">
            {`Your performance for ${yesterday().format('MMMM Do, YYYY')}`}
        </div>
    )
}

export default makeWaitForData(StreakMessaging, STREAK_DEPS, "StreakMessaging", StreakSkeleton);